import { BsBroadcast } from "react-icons/bs"
import { useRecoilValue, useRecoilState } from "recoil"
import { Box, Flex } from "@chakra-ui/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Icon } from "../../common/icons"
import LocalButton from "./_localButton"
import { useBoolean } from "../../../hooks/useBoolean"
import { useClickOutside } from "../../../hooks/useClickOutside"
import StreamContainer from "./stream-container"
import Modal from "../../common/modal"
import { brandingSelector } from "../../../recoil/atoms/room"
import {
  streamsLoaderAtom,
  streamsStartAtom,
  connectBotStreamAtom,
  streamingUserIdAtom
} from "../../../recoil/atoms/streams"
import { useTranslation } from "react-i18next"
import { blendWithWhiteBackground } from "../../../util"
import { recordingStartAtom } from "../../../recoil/atoms/recording"
import { Tooltip } from "../../common/tooltip"
import LocalButtonLoader from "./LocalButtonLoader"

const UserPermission = window.MeetOnline?.UserPermission

const RECORD_SPINNER_DURATION = 3000

const StreamButton = ({ isWithoutTitle, localUserProperties, isFirst = false, isLast = false }) => {
  const { t } = useTranslation()
  const roomStyles = useRecoilValue(brandingSelector)

  const streamRef = useRef<HTMLDivElement>(null)
  const [isOpenStreamModal, setIsOpenStreamModal] = useBoolean(false)
  const [isStreamsLoader, setSteamsLoader] =
    useRecoilState<boolean>(streamsLoaderAtom)
  const [isStreamsStart, setSteamsStart] =
    useRecoilState<boolean>(streamsStartAtom)
  const [isConnectStreamBot] = useRecoilState<boolean>(connectBotStreamAtom)
  const [isDisconnectStreamBot, setDisconnectStreamBot] = useState(false)
  const streamingUserId = useRecoilValue(streamingUserIdAtom)
  const isRecordingStart = useRecoilValue(recordingStartAtom)

  useClickOutside(streamRef, setIsOpenStreamModal.off)

  const isButtonHidden = useCallback(
    (p: number) => {
      return (localUserProperties?.permissions & p) === 0
    },
    [localUserProperties?.permissions]
  )

  // is other user started streaming
  const isNonStreamUser = isStreamsStart && streamingUserId !== localUserProperties?.localUserId

  const isStreamsDisabled = useMemo(() => {
    // if recording is ON, disable the stream button
    if (isRecordingStart) return true

    // Disable if another user start the streaming
    if(isNonStreamUser) return true

    // check user permission
    return isButtonHidden(UserPermission.canStreamSessions)
  }, [isButtonHidden, isRecordingStart, isNonStreamUser])

  useEffect(() => {
    if (isConnectStreamBot) {
      let timer = setTimeout(() => {
        setSteamsLoader(false)
        setSteamsStart(true)
      }, RECORD_SPINNER_DURATION)
      return () => clearTimeout(timer)
    }

    if (isDisconnectStreamBot) {
      let timer = setTimeout(() => {
        setSteamsLoader(false)
        setSteamsStart(false)
        setDisconnectStreamBot(false)
      }, RECORD_SPINNER_DURATION)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnectStreamBot, isDisconnectStreamBot])


  const dataStreamIcon = useMemo(() => {
    return {
      icons: [<Icon src={BsBroadcast} />, <Icon src={BsBroadcast} />],
      // if local user is nonStreamingUser show "Streaming"
      names: [
        isStreamsStart
          ? // ON condition
            isStreamsLoader
            ? <LocalButtonLoader enabled={isDisconnectStreamBot} />
            : isNonStreamUser
            ? t("Streaming")
            : t("Stop streaming")
          : // OFF condition
          isStreamsLoader
          ? <LocalButtonLoader enabled={isDisconnectStreamBot} />
          : t("Stream")
      ]
    }
  }, [isStreamsLoader, isStreamsStart, isNonStreamUser, t, isDisconnectStreamBot])

  return (
    <Flex
      position="relative"
      ref={streamRef}
      width="calc((66.66vw - 100px) / 7)"
      display={
        isButtonHidden(UserPermission.canStreamSessions) ? "none" : "flex"
      }
    >
      <Modal
        isOutsideClickEnabled={false}
        isOpen={isOpenStreamModal}
        onClose={setIsOpenStreamModal.off}
        customstyles={{
          wrapperStyles: {
            background: blendWithWhiteBackground(
              roomStyles?.servicePanelElementModalBackground
            ),
            boxShadow: `0 0 15px ${
              roomStyles?.servicePanelElementBorder ?? "#e2e8f0"
            }`
          }
        }}
      >
        <StreamContainer setDisconnectStreamBot={setDisconnectStreamBot} />
      </Modal>

      {/* Show tooltip when recording is ON */}
      <Tooltip
        label={
          t("To start streaming, please stop recording first. When you start streaming a fresh recording will start automatically.")
        }
        placement="top"
        isDisabled={!isRecordingStart}
      >
        <Box width="100%">
          <LocalButton
            id="5"
            names={dataStreamIcon.names}
            setter={setIsOpenStreamModal.toggle}
            icons={dataStreamIcon.icons}
            isLast={isLast}
            isFirst={isFirst}
            isWithoutTitle={isWithoutTitle}
            isActive={isOpenStreamModal || isStreamsStart}
            isHidden={isButtonHidden(UserPermission.canStreamSessions)}
            showSelecedStyling={false}
            haveModal={true}
            isDisabled={isStreamsDisabled}
          />
        </Box>
      </Tooltip>
    </Flex>
  )
}

export default StreamButton
